import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { CSSProperties, FC, Fragment, ReactNode } from 'react'

import { cn } from '@/app/utils'

export type AppModalProps = {
  header?: ReactNode
  title?: ReactNode
  children?: ReactNode
  opened: boolean
  notClosable?: boolean
  onClose: () => void
  className?: string
  style?: CSSProperties
  overlayClassName?: string
  headerWrapperClassName?: string
  headerClassName?: string
  closeButtonClassName?: string
  closeButtonIconClassName?: string
  contentClassName?: string
}

const AppModal: FC<AppModalProps> = ({
  header,
  title,
  children,
  opened,
  onClose,
  className,
  style,
  notClosable,
  overlayClassName,
  headerWrapperClassName,
  headerClassName,
  closeButtonClassName,
  closeButtonIconClassName,
  contentClassName
}) => {
  const overlayClasses = cn(
    'fixed inset-0 bg-black/50 xs:bg-transparent',
    overlayClassName
  )
  const dialogPanelClasses = cn(
    'relative flex max-h-full max-w-full flex-col overflow-y-auto rounded-16 bg-grey-10',
    'xs:h-full xs:w-full xs:max-w-full xs:rounded-0',
    className
  )
  const headerWrapperClasses = cn(
    'flex w-full items-center gap-8 py-20 pb-8 pl-32 pr-20',
    headerWrapperClassName
  )
  const closeButtonClasses = cn(
    'outline-none ml-auto flex h-40 w-40 items-center justify-center rounded-100 bg-[rgba(96,107,118,0.10)] p-8 transition-colors hover:bg-grey-30 xs:p-4 xs:h-28 xs:w-28',
    {
      'pointer-events-none invisible opacity-0': notClosable
    },
    closeButtonClassName
  )
  const closeButtonIconClasses = cn(
    'h-24 w-24 text-bronze-70',
    closeButtonIconClassName
  )
  const contentClasses = cn(
    'flex grow flex-col overflow-y-auto px-32 pb-32',
    contentClassName
  )

  return (
    <Transition appear show={opened} as={Fragment}>
      <Dialog
        as="div"
        className={cn('relative z-50', { 'cursor-pointer': !notClosable })}
        onClose={() => !notClosable && onClose()}
      >
        <Transition.Child
          enter="duration-300"
          enterFrom="opacity-0"
          leave="duration-300"
          leaveTo="opacity-0"
        >
          <div className={overlayClasses} />
        </Transition.Child>

        <div className="fixed inset-0 z-50">
          <div className="flex h-full max-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Panel className={dialogPanelClasses} style={style}>
                <div className={headerWrapperClasses}>
                  {header && (
                    <Dialog.Title
                      className={cn(
                        'font-bold text-28 lg:text-28 sm:text-24',
                        headerClassName
                      )}
                    >
                      {header}
                    </Dialog.Title>
                  )}

                  <button
                    aria-label="Закрыть модальное окно"
                    title="Закрыть"
                    className={closeButtonClasses}
                    onClick={() => !notClosable && onClose()}
                  >
                    <XMarkIcon className={closeButtonIconClasses} />
                  </button>
                </div>

                {title && (
                  <Dialog.Title className="px-32 font-bold text-28 lg:text-28 xs:text-20">
                    {title}
                  </Dialog.Title>
                )}

                <div className={contentClasses}>{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default AppModal
